import React, {
  createContext, useContext, useState, useEffect
} from 'react';
import { node, func } from 'prop-types';

import { usePromoModel } from './PromoModelProvider';
import {
  emptyPromoCart, transformCartModelToPromoCart
} from '../utils/promo-cart-utils';
import { useCart } from '../hooks/useCart';

export const PromoCartContext = createContext();

export const usePromoCart = () => useContext(PromoCartContext);

export const PromoCartProvider = ({ setCurrentPromoTier, children }) => {

  const {
    isBogo,
    isMsb,
    isBmsm,
    minPurchaseQuantity,
    minPurchaseAmount,
    anchorItemId,
    storeId,
    rewardTiers,
    allFulfillableProductsAndAnchor,
    singleItemIdToDisplay,
    displayableItemIds,
  } = usePromoModel();
  const {
    CartModel,
    addToCart,
    updateCart,
    isCartLoading,
    setIsCartLoading,
    freezeCart
  } = useCart();

  const [promoCart, setPromoCart] = useState(emptyPromoCart);
  const [bogoSelectedProduct, setBogoSelectedProduct] = useState(null);

  useEffect(() => {
    setBogoSelectedProduct(singleItemIdToDisplay ? allFulfillableProductsAndAnchor.get(singleItemIdToDisplay) : null);
  }, [singleItemIdToDisplay, allFulfillableProductsAndAnchor]);

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('buybox.bogoSelectedProduct', bogoSelectedProduct);
  }, [bogoSelectedProduct]);

  useEffect(() => {
    if (promoCart?.freeItemInCart) {
      setBogoSelectedProduct(null);
    }
  }, [promoCart]);

  useEffect(() => {
    if (CartModel && anchorItemId) {
      const newPromoCart = transformCartModelToPromoCart({
        rawCartModel: CartModel,
        isBogo,
        isMsb,
        isBmsm,
        minPurchaseQuantity,
        minPurchaseAmount,
        displayableItemIds,
        anchorItemId,
        bogoSelectedProduct,
        rewardTiers,
        allFulfillableProductsAndAnchor,
      });
      if (newPromoCart) setPromoCart(newPromoCart);
    }
  }, [
    CartModel,
    isBogo,
    isMsb,
    isBmsm,
    minPurchaseQuantity,
    minPurchaseAmount,
    anchorItemId,
    bogoSelectedProduct,
    rewardTiers,
    allFulfillableProductsAndAnchor,
    displayableItemIds
  ]);

  const currentPromoTier = promoCart.currentPromoTier;

  // We need to pass currentPromoTier up to ModelProvider to expose correct current tier data
  useEffect(() => { setCurrentPromoTier(currentPromoTier); }, [setCurrentPromoTier, currentPromoTier]);

  if (!anchorItemId || !storeId || !promoCart) return null;
  const contextValue = {
    // promoCart is only mutated as a sideEffect of updating rawCartModel
    promoCartValue: promoCart.promoCartValue,
    promoCartQuantity: promoCart.promoCartQuantity,
    promoCartItemsObj: promoCart.promoCartItemsObj,
    freeItemInCart: promoCart.freeItemInCart,
    promoSuccessLevel: promoCart.promoSuccessLevel,
    // Percentage of promotion completed. For BMSM percentage of current tier completed
    progressMade: promoCart.progressMade,
    isPromoAchieved: promoCart.isPromoAchieved,
    currentPromoTier: promoCart.currentPromoTier,
    bogoSelectedProduct,
    setBogoSelectedProduct,
    CartModel,
    addToCart,
    updateCart,
    freezeCart,
    isCartLoading,
    setIsCartLoading,
  };

  return (
    <PromoCartContext.Provider value={contextValue}>
      {children}
    </PromoCartContext.Provider>
  );
};

PromoCartProvider.propTypes = {
  children: node.isRequired,
  setCurrentPromoTier: func.isRequired,
};
